/* eslint-disable sonarjs/cognitive-complexity */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BOOKING_CALENDAR } from '../../../../brands/default/content/components/deal';
import { showEstimated } from '../../../../helpers/adminFee';
import { isNextMonth } from '../../../../helpers/booking';
import Spinner from '../../../_generic/spinner/Spinner';
import BookingCalendarCells from './BookingCalendarCells';
import BookingCalendarCellsBeginEnd from './BookingCalendarCellsBeginEnd';
import BookingCalendarCheckInOut from './BookingCalendarCheckInOut';
import BookingCalendarNav from './BookingCalendarNav';
import BookingCalendarNavTop from './BookingCalendarNavTop';

const BookingCalendar = ({
  beginEndDates,
  cells,
  deal,
  displayMonth,
  displayYear,
  errorCYF,
  isBookingCalendarOnRedemption,
  isCheckinNearMonthEnd,
  handleOnHoverDate,
  handleOnHoverOffCells,
  handleOnNext,
  handleOnPrevious,
  next,
  onSelect,
  onSelectEndDate,
  prev,
  resetCalendar,
}) => {
  // calendar view selection
  const [basketTotal] = useSelector((state) => [state?.user?.basketTotal]);
  const [estimatedText, setEstimatedText] = useState(null);
  useEffect(() => {
    if (deal?.products[0]?.adminFee > 0) {
      setEstimatedText(
        showEstimated({
          calendar: true,
          finalOrderAmount: basketTotal?.adminFeeApplicableOrderAmount || 0,
          price: deal.price,
          totalAdminFee: basketTotal?.totalAdminFee || 0,
        }),
      );
    }
  }, [basketTotal, deal]);

  const { bookingNoResults, bookableAtRedemption } = BOOKING_CALENDAR;

  if (errorCYF) {
    return (
      <>
        <div className="booking-calendar--CYF-error">{bookingNoResults}</div>
        <style jsx>{`
          .booking-calendar--CYF-error {
            font-style: italic;
            margin-bottom: 30px;
            margin-top: 30px;
            text-align: center;
          }
        `}</style>
      </>
    );
  }

  if (!cells) {
    return (
      <>
        <div className="booking-calendar--loading">
          <Spinner />
        </div>
        <style jsx>{`
          .booking-calendar--loading {
            position: relative;
            min-height: 480px; /* 5 row calendar (most common) */
          }
        `}</style>
      </>
    );
  }

  const onHoverDate = (date) => {
    if (handleOnHoverDate) handleOnHoverDate(date);
  };

  const onHoverOffCells = () => {
    if (handleOnHoverOffCells) handleOnHoverOffCells();
  };

  const onNext = () => {
    if (handleOnNext) handleOnNext();
  };
  const onPrevious = () => {
    if (handleOnPrevious) handleOnPrevious();
  };

  const onMonthChange = (key) => {
    const isNext = isNextMonth(key, displayMonth);

    if (isNext) {
      if (handleOnNext) handleOnNext();
    } else {
      // eslint-disable-next-line no-lonely-if
      if (handleOnPrevious) handleOnPrevious();
    }
  };

  return (
    <>
      <BookingCalendarNavTop />
      {beginEndDates && (
        <>
          <BookingCalendarCheckInOut
            beginEndDates={beginEndDates}
            onSelect={onSelectEndDate}
            resetCalendar={resetCalendar}
          />
        </>
      )}
      <BookingCalendarNav
        disabledNext={!next}
        disabledPrev={!prev}
        isCheckinNearMonthEnd={isCheckinNearMonthEnd}
        month={displayMonth}
        next={next}
        onNext={onNext}
        onPrev={onPrevious}
        prev={prev}
        year={displayYear}
      />

      {!beginEndDates && (
        <BookingCalendarCells
          cells={cells}
          isBookingCalendarOnRedemption={isBookingCalendarOnRedemption}
          onMonthChange={onMonthChange}
          onSelect={onSelect}
        />
      )}
      {beginEndDates && (
        <BookingCalendarCellsBeginEnd
          beginEndDates={beginEndDates}
          cells={cells}
          onHoverDate={onHoverDate}
          onHoverOffCells={onHoverOffCells}
          onMonthChange={onMonthChange}
          onSelect={onSelect}
        />
      )}
      <p className="booking-calendar__asterix">{estimatedText || null}</p>
      {isBookingCalendarOnRedemption && (
        <p className="booking-calendar__asterix">{bookableAtRedemption}</p>
      )}

      <style jsx>{`
        .booking-calendar__asterix {
          font-size: 12px;
          margin-bottom: 20px;
        }
      `}</style>
    </>
  );
};

BookingCalendar.propTypes = {
  beginEndDates: PropTypes.object,
  cells: PropTypes.array,
  deal: PropTypes.object,
  displayMonth: PropTypes.string,
  displayYear: PropTypes.number,
  errorCYF: PropTypes.bool,
  handleOnHoverDate: PropTypes.func,
  handleOnHoverOffCells: PropTypes.func,
  handleOnNext: PropTypes.func,
  handleOnPrevious: PropTypes.func,
  isBookingCalendarOnRedemption: PropTypes.bool,
  isCheckinNearMonthEnd: PropTypes.bool,
  next: PropTypes.object,
  onSelect: PropTypes.func,
  onSelectEndDate: PropTypes.func,
  prev: PropTypes.object,
  resetCalendar: PropTypes.func,
};

BookingCalendar.defaultProps = {
  isBookingCalendarOnRedemption: false,
};

export default BookingCalendar;
